import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, ShieldCheck, Bell, Database, Loader2, UsersRound, SlidersHorizontal, Wrench } from "lucide-react";
import { TeamSection } from "@/components/settings/TeamSection";
import { PolicyControls } from "@/components/policies/PolicyControls";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { key: "admin", label: "Admin", icon: Wrench },
  { key: "team", label: "Team", icon: UsersRound },
  { key: "policy-controls", label: "Policy Controls", icon: SlidersHorizontal },
];

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="rounded-[14px] border border-[#222] bg-[#111111] p-4 card-shadow sm:p-6">
    <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[rgba(79,142,247,0.12)]"><Icon size={15} className="text-[#4F8EF7]" /></div>
      <div>
        <h2 className="text-[14px] font-semibold tracking-tight">{title}</h2>
        <p className="text-[11.5px] text-[#A3A3A3]">{desc}</p>
      </div>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const AdminTab = () => {
  const [settings, setSettings] = useState(null);
  const [name, setName] = useState("");
  const [threshold, setThreshold] = useState("");
  const [saving, setSaving] = useState(false);
  const [reseeding, setReseeding] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setName(r.data.workspace_name);
      setThreshold(String(r.data.approval_threshold));
    }).catch(() => {});
  }, []);

  const save = async (updates, msg = "Settings saved") => {
    setSaving(true);
    try {
      const r = await api.put("/settings", updates);
      setSettings(r.data);
      toast.success(msg);
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const reseed = async () => {
    setReseeding(true);
    try {
      await api.post("/admin/reseed");
      toast.success("Demo data reset — refresh any page to see it");
    } catch {
      toast.error("Reseed failed");
    } finally {
      setReseeding(false);
    }
  };

  if (!settings) {
    return <div className="space-y-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 rounded-[14px] bg-[#141414]" />)}</div>;
  }

  return (
    <div className="space-y-4" data-testid="settings-admin-tab">
      <Section icon={Building2} title="Workspace" desc="Your organization identity across Aria.">
        <div className="flex flex-wrap gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-workspace-name-input"
            className="h-9 max-w-xs border-[#2a2a2a] bg-[#161616] text-[13px] text-white" />
          <Button onClick={() => save({ workspace_name: name }, "Workspace renamed")} disabled={saving || !name.trim()}
            data-testid="settings-save-workspace-button"
            className="h-9 bg-[#1f1f1f] px-4 text-[12.5px] text-white hover:bg-[#2a2a2a]">
            {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
          </Button>
        </div>
      </Section>

      <Section icon={ShieldCheck} title="Approvals & AI autonomy" desc="How much Aria does on its own before a human sees anything.">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-[#6b6b6b]">Auto-approval threshold (USD)</label>
            <div className="mt-1.5 flex gap-2">
              <Input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="settings-threshold-input"
                className="h-9 border-[#2a2a2a] bg-[#161616] text-[13px] text-white" />
              <Button onClick={() => save({ approval_threshold: parseFloat(threshold) || 0 }, "Threshold updated")} disabled={saving}
                data-testid="settings-save-threshold-button"
                className="h-9 bg-[#1f1f1f] px-3 text-[12.5px] text-white hover:bg-[#2a2a2a]">Save</Button>
            </div>
            <p className="mt-1.5 text-[10.5px] text-[#6b6b6b]">Expenses under this amount with &gt;90% confidence are auto-approved.</p>
          </div>
          <div>
            <label className="text-[11px] font-medium uppercase tracking-wide text-[#6b6b6b]">AI autonomy level</label>
            <Select value={settings.ai_autonomy} onValueChange={(v) => save({ ai_autonomy: v }, `Autonomy set to ${v}`)}>
              <SelectTrigger className="mt-1.5 h-9 border-[#2a2a2a] bg-[#161616] text-[13px]" data-testid="settings-autonomy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[#222] bg-[#161616] text-white">
                <SelectItem value="autopilot">Autopilot — Aria handles everything</SelectItem>
                <SelectItem value="supervised">Supervised — humans review exceptions</SelectItem>
                <SelectItem value="manual">Manual — every expense needs approval</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section icon={Bell} title="Notifications" desc="Where Aria reaches people.">
        <div className="space-y-3">
          {[
            { key: "notify_slack", label: "Slack notifications", desc: "Aria DMs employees about receipts and approvals" },
            { key: "notify_email", label: "Email digests", desc: "Daily summary to finance leads" },
            { key: "auto_export", label: "Automatic nightly ERP export", desc: "Push approved expenses to NetSuite at 02:00 UTC" },
          ].map((s) => (
            <div key={s.key} className="flex items-center justify-between rounded-xl border border-[#1e1e1e] bg-[#141414] px-4 py-3">
              <div>
                <div className="text-[12.5px] font-medium text-white">{s.label}</div>
                <div className="text-[10.5px] text-[#6b6b6b]">{s.desc}</div>
              </div>
              <Switch checked={settings[s.key]} onCheckedChange={(v) => save({ [s.key]: v }, `${s.label} ${v ? "enabled" : "disabled"}`)}
                data-testid={`settings-toggle-${s.key}`} className="data-[state=checked]:bg-[#4F8EF7]" />
            </div>
          ))}
        </div>
      </Section>

      <Section icon={Database} title="Demo data" desc="Reset the workspace to its original seeded state.">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" data-testid="settings-reseed-button"
              className="h-9 border-[rgba(239,68,68,0.3)] bg-transparent px-4 text-[12.5px] text-[#F87171] hover:bg-[rgba(239,68,68,0.08)] hover:text-[#F87171]">
              {reseeding ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null} Reset demo data
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="border-[#222] bg-[#141414] text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Reset all demo data?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#A3A3A3]">
                This wipes expenses, conversations, policies and activity, then reseeds the original 20-employee dataset. Anything you created will be lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-[#2a2a2a] bg-transparent text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={reseed} data-testid="settings-reseed-confirm-button" className="bg-[#EF4444] text-white hover:bg-[#f25555]">
                Reset data
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Section>
    </div>
  );
};

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const tab = TABS.some((t) => t.key === urlTab) ? urlTab : "admin";
  const setTab = (key) => setSearchParams(key === "admin" ? {} : { tab: key }, { replace: true });

  return (
    <div className="max-w-3xl space-y-4" data-testid="settings-page">
      <div className="flex flex-wrap items-center gap-1" data-testid="settings-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-1.5 text-[12.5px] transition-colors ${
              tab === t.key ? "bg-[#1f1f1f] text-white" : "text-[#A3A3A3] hover:text-white"
            }`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "admin" && <AdminTab />}
      {tab === "team" && (
        <Section icon={UsersRound} title="Team" desc="Everyone in the Acme Corp workspace, and their roles.">
          <TeamSection />
        </Section>
      )}
      {tab === "policy-controls" && (
        <Section icon={SlidersHorizontal} title="Policy controls" desc="Who can create and edit policies — and what approvals are needed.">
          <PolicyControls />
        </Section>
      )}
    </div>
  );
}
